// src/components/admin/page-management/PageTemplatesManager.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AlertCircle, Check } from 'lucide-react';

// Import existing page templates
import AboutUsTemplate from './templates/AboutUsTemplate';
// import ProductTemplate from './templates/ProductTemplate';
import HomeTemplate from './templates/HomeTemplate';
// import ShopTemplate from './templates/ShopTemplate';

// Template registry - add all available templates here
const templateRegistry = {
  // Original templates
  aboutUs: AboutUsTemplate,
  // contact: ProductTemplate,
  home: HomeTemplate,
  // shop: ShopTemplate
};

const PageTemplatesManager = ({ pageId, onTemplateApplied, setError }) => {
  const [applying, setApplying] = useState(false);
  const [appliedTemplate, setAppliedTemplate] = useState(null);
  const [confirmation, setConfirmation] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Group templates by category for better organization
  const templateCategories = {
    "Content Pages": ['home', 'aboutUs'],
    "System Pages": ['contact', 'shop']
  };

  // const applyTemplate = async (templateKey) => {
  //   if (!pageId) {
  //     setError('Save the page first before applying a template');
  //     return;
  //   }
  
  //   // Get confirmation before proceeding
  //   if (!confirmation) {
  //     setSelectedTemplate(templateKey);
  //     setConfirmation(true);
  //     return;
  //   }
  
  //   try {
  //     setApplying(true);
  //     const templateModule = templateRegistry[templateKey];
      
  //     if (!templateModule || !templateModule.sections) {
  //       throw new Error(`Template "${templateKey}" not found or has no sections defined`);
  //     }
      
  //     const template = templateModule;
  //     let containerId = null;
  
  //     // Deep clone the template sections to avoid modifying the original
  //     const sectionsCopy = JSON.parse(JSON.stringify(template.sections));
  
  //     // Add sections one by one to the page
  //     for (const section of sectionsCopy) {
  //       // If this is a container section, save its ID for child sections
  //       if (section.type === 'container') {
  //         // Add the container first
  //         const containerResponse = await axios.post(`/api/admin/pages/${pageId}/sections`, section);
  //         // Find the newly created container
  //         const newSections = containerResponse.data.sections;
  //         const newContainer = newSections[newSections.length - 1]; // Get the last added section
  //         containerId = newContainer._id;
  //       } 
  //       // If this section has a parent placeholder, replace it with the actual container ID
  //       else if (section.parentId === '[CONTAINER_ID]' && containerId) {
  //         section.parentId = containerId;
  //         await axios.post(`/api/admin/pages/${pageId}/sections`, section);
  //       }
  //       // Otherwise, add as normal
  //       else {
  //         await axios.post(`/api/admin/pages/${pageId}/sections`, section);
  //       }
        
  //       // Add a small delay to ensure database consistency
  //       await new Promise(resolve => setTimeout(resolve, 100));
  //     }
  
  //     setAppliedTemplate(templateKey);
  //     onTemplateApplied(template.title);
  //     setConfirmation(false);
  //     setSelectedTemplate(null);
  //   } catch (err) {
  //     console.error('Error applying template:', err);
  //     setError('Failed to apply template: ' + (err.response?.data?.error || err.message));
  //   } finally {
  //     setApplying(false);
  //   }
  // };
  // In PageTemplatesManager.js, update the applyTemplate function:
  const applyTemplate = async (templateKey) => {
    if (!pageId) {
      setError('Save the page first before applying a template');
      return;
    }

    // Get confirmation before proceeding
    if (!confirmation) {
      setSelectedTemplate(templateKey);
      setConfirmation(true);
      return;
    }

    try {
      setApplying(true);
      const templateModule = templateRegistry[templateKey];
      
      if (!templateModule || !templateModule.sections) {
        throw new Error(`Template "${templateKey}" not found or has no sections defined`);
      }
      
      const template = templateModule;
      let containerId = null;

      // Deep clone the template sections to avoid modifying the original
      const sectionsCopy = JSON.parse(JSON.stringify(template.sections));

      // Add sections one by one to the page
      for (const section of sectionsCopy) {
        // If this is a container section, save its ID for child sections
        if (section.type === 'container') {
          // Add the container first
          const containerResponse = await axios.post(`/api/admin/pages/${pageId}/sections`, section);
          // Find the newly created container
          const newSections = containerResponse.data.sections;
          const newContainer = newSections[newSections.length - 1]; // Get the last added section
          containerId = newContainer._id;
        } 
        // If this section has a parent placeholder, replace it with the actual container ID
        else if (section.parentId === '[CONTAINER_ID]' && containerId) {
          section.parentId = containerId;
          await axios.post(`/api/admin/pages/${pageId}/sections`, section);
        }
        // Otherwise, add as normal
        else {
          await axios.post(`/api/admin/pages/${pageId}/sections`, section);
        }
        
        // Add a small delay to ensure database consistency
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Fetch the updated page data to reflect the new sections
      const updatedPageResponse = await axios.get(`/api/admin/pages/${pageId}`);
      onTemplateApplied(template.title, updatedPageResponse.data);
      
      setAppliedTemplate(templateKey);
      setConfirmation(false);
      setSelectedTemplate(null);
    } catch (err) {
      console.error('Error applying template:', err);
      setError('Failed to apply template: ' + (err.response?.data?.error || err.message));
    } finally {
      setApplying(false);
    }
  };
  
  // Cancel the confirmation
  const cancelApply = () => {
    setConfirmation(false);
    setSelectedTemplate(null);
  };

  // If we're in confirmation mode, show the confirmation UI
  if (confirmation && selectedTemplate) {
    const templateModule = templateRegistry[selectedTemplate];
    if (!templateModule) {
      return <div>Template not found</div>;
    }
    
    const template = templateModule;
    
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
        <div className="flex items-start">
          <AlertCircle className="w-6 h-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-lg text-yellow-800 mb-2">Apply "{template.title}" Template?</h3>
            <p className="text-yellow-700 mb-4">
              This will add {template.sections.length} new sections to your page. Any existing content will remain.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => applyTemplate(selectedTemplate)}
                disabled={applying}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center"
              >
                {applying ? 'Applying...' : 'Apply Template'}
              </button>
              <button
                onClick={cancelApply}
                disabled={applying}
                className="border border-yellow-600 text-yellow-700 hover:bg-yellow-100 px-4 py-2 rounded text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Already applied template confirmation
  if (appliedTemplate) {
    const templateModule = templateRegistry[appliedTemplate];
    if (!templateModule) {
      return <div>Template not found</div>;
    }
    
    const template = templateModule;
    
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
        <div className="flex items-start">
          <Check className="w-6 h-6 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-lg text-green-800 mb-2">Template Applied Successfully</h3>
            <p className="text-green-700 mb-2">
              The "{template.title}" template has been added to your page.
            </p>
            <button
              onClick={() => setAppliedTemplate(null)}
              className="text-green-700 hover:text-green-900 text-sm font-medium"
            >
              Apply another template
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Default template selection UI
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold mb-4">Page Templates</h3>
      <p className="text-gray-600 mb-6">
        Apply a template to quickly create a professionally designed page. You can customize all sections after applying.
      </p>
      
      {/* Render templates by category */}
      {Object.entries(templateCategories).map(([category, templateKeys]) => (
        <div key={category} className="mb-8">
          <h4 className="text-lg font-medium text-gray-800 mb-3">{category}</h4>
          <div className="grid sm:grid-cols-2 gap-4">
            {templateKeys.map(key => {
              const templateModule = templateRegistry[key];
              if (!templateModule) return null;
              
              const template = templateModule;
              return (
                <div 
                  key={key}
                  className="border rounded-lg p-6 hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
                  onClick={() => applyTemplate(key)}
                >
                  <h4 className="font-semibold text-lg mb-2">{template.title}</h4>
                  <p className="text-gray-600 text-sm mb-3">{template.description}</p>
                  <p className="text-xs text-gray-500">{template.sections.length} sections</p>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PageTemplatesManager;




